import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { StepProgress } from "@/components/fionova/StepProgress";
import { EllieBridge } from "@/components/journey/EllieBridge";
import { PrimaryButton, Eyebrow } from "@/components/fionova/Primitives";
import { generateXray } from "@/services/unsaidService";
import { unsaidStore, transcriptHash } from "@/lib/unsaidStore";
import { trackSessionOnce } from "@/lib/analytics";

const XSection = ({ label, title, children, emphasized, delay = 0, testId }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className={`rounded-3xl p-6 ring-1 ring-border ${emphasized ? "bg-primary/[0.06]" : "bg-card"}`}
  >
    <p className={`eyebrow text-[11px] ${emphasized ? "text-primary" : "text-muted-foreground"}`}>{label}</p>
    {title && (
      <h3 className={`mt-2 font-heading ${emphasized ? "text-2xl" : "text-xl"} font-medium tracking-tight text-foreground`}>
        {title}
      </h3>
    )}
    <p className="mt-2 text-base leading-relaxed text-foreground/90">{children}</p>
  </motion.div>
);

export default function EmotionalXray() {
  const navigate = useNavigate();
  const [outcome] = useState(() => unsaidStore.getOutcome());
  const [status, setStatus] = useState("loading"); // loading | reveal | error | missing
  const [xray, setXray] = useState(null);
  const startedRef = useRef(false);

  const hasHandoff = !!(outcome && (outcome.transcript || []).some((m) => m.role === "user"));

  const load = useCallback(async () => {
    if (!hasHandoff) { setStatus("missing"); return; }
    const hash = transcriptHash(outcome.transcript);
    const stored = unsaidStore.getXrayOutcome();
    const fallback = stored && stored.hash === hash ? stored.xray : null;
    const cached = unsaidStore.getXrayCache(hash) || fallback;
    if (cached) { unsaidStore.setXrayCache(hash, cached); setXray((p) => p || cached); setStatus((s) => (s === "reveal" ? s : "reveal")); return; }
    setStatus((s) => (s === "reveal" ? s : "loading"));
    const result = await generateXray(outcome);
    if (!result.ok) { setStatus((s) => (s === "reveal" ? s : "error")); return; }
    unsaidStore.setXrayCache(hash, result.xray);
    unsaidStore.saveXrayOutcome({ xray: result.xray, hash, fromUnsaidAt: outcome.savedAt || null });
    setXray((p) => p || result.xray);
    setStatus((s) => (s === "reveal" ? s : "reveal"));
  }, [outcome, hasHandoff]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    load();
  }, [load]);

  // GA4: Emotional X-Ray stage completed (step 2), then advance.
  const continueToPlan = () => {
    trackSessionOnce("step_xray", "onboarding_step_completed", { step_name: "emotional_xray", step_number: 2 });
    navigate("/today-plan");
  };

  if (status === "missing") {
    return (
      <OnboardingLayout testId="page-emotional-xray" contentClassName="items-center justify-center py-10 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Let's start with a conversation.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          The Emotional X-Ray builds on The Unsaid. When you're ready, we can begin there together.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="xray-begin-unsaid" onClick={() => navigate("/the-unsaid")}>Begin The Unsaid</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  if (status === "loading") {
    return (
      <OnboardingLayout testId="page-emotional-xray" contentClassName="items-center justify-center py-10 text-center">
        <div className="flex items-center gap-1.5" data-testid="xray-loading">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <p className="mt-6 text-base text-muted-foreground">Looking a little deeper…</p>
      </OnboardingLayout>
    );
  }

  if (status === "error") {
    return (
      <OnboardingLayout testId="page-emotional-xray" contentClassName="items-center justify-center py-10 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Let me take another moment.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          I wasn't quite able to gather my thoughts. Give me a moment to try again.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="xray-retry" onClick={load}>Try again</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // reveal
  const limited = xray?.confidence === "limited";
  const underneath = xray?.underneath && xray.underneath.detail ? xray.underneath : null;

  if (xray?.crisis) {
    return (
      <div data-testid="page-emotional-xray" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
        <div className="mx-auto w-full max-w-[560px] px-6 py-14 sm:px-8">
          <div className="text-center" data-testid="xray-crisis">
            <Eyebrow>A moment of care</Eyebrow>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-foreground">
              You matter, and you deserve support.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-foreground/90">
              {xray?.primary_state?.detail}
            </p>
            <div className="mt-8 rounded-3xl bg-card p-6 text-left ring-1 ring-border">
              <p className="text-base leading-relaxed text-foreground/90">
                If you're thinking about harming yourself or feel in danger, please reach out now —
                you don't have to carry this alone.
              </p>
              <a
                href="tel:988"
                data-testid="xray-crisis-call"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-base font-semibold text-primary-foreground transition-all hover:bg-primary/90"
              >
                Call or text 988
              </a>
              <p className="mt-3 text-center text-sm text-muted-foreground">
                US Suicide &amp; Crisis Lifeline · available 24/7
              </p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <button data-testid="xray-continue" onClick={continueToPlan}
              className="text-base font-medium text-muted-foreground transition-colors hover:text-foreground">
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-emotional-xray" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto w-full max-w-[560px] px-6 py-12 sm:px-8">
        <StepProgress total={5} current={2} className="mb-10" />
        <EllieBridge feature="xray" className="mb-8" />

        <div className="text-center">
          <Eyebrow>Emotional X-Ray</Eyebrow>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-foreground">
            What seems to be happening right now
          </h1>
        </div>

        <div className="mt-10 space-y-5">
          <XSection testId="xray-primary" label="Right now" title={xray?.primary_state?.label} emphasized delay={0}>
            {xray?.primary_state?.detail}
          </XSection>

          {!limited && xray?.driver?.detail && (
            <XSection testId="xray-driver" label="What may be driving it" title={xray.driver.label} delay={0.08}>
              {xray.driver.detail}
            </XSection>
          )}

          {!limited && underneath && (
            <XSection testId="xray-underneath" label="What may be underneath" title={underneath.label} delay={0.16}>
              {underneath.detail}
            </XSection>
          )}

          {xray?.need?.detail && (
            <XSection testId="xray-need" label="What you may need" title={xray.need.label} delay={0.24}>
              {xray.need.detail}
            </XSection>
          )}

          {xray?.eq_opportunity?.detail && (
            <XSection testId="xray-eq" label="Your EQ opportunity" title={xray.eq_opportunity.label} emphasized delay={0.32}>
              {xray.eq_opportunity.detail}
            </XSection>
          )}
        </div>

        <div className="mt-12">
          <PrimaryButton data-testid="xray-continue" onClick={continueToPlan}>
            Continue
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
